import { AppIcon } from '@/components/ui/app-icon';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { iconCheck } from '@/lib/icons';
import { cn } from '@/lib/utils';

/**
 * The v4 plans side by side, from the API's plan catalog
 * (providers/billing/pricing_v4.py, utils/search_allowance.py): Free gets 5
 * searches, Starter 1,000 a month, Pro unlimited. CRM sync, CRM automation and
 * in-CRM enrichment start on Pro.
 */

type Cell = string | boolean;

const PLANS = ['Free', 'Starter', 'Pro', 'Enterprise'] as const;

const ROWS: { label: string; cells: [Cell, Cell, Cell, Cell] }[] = [
  {
    label: 'Price',
    cells: ['$0', '$49 / seat / mo', '$89 / seat / mo', 'Custom'],
  },
  {
    label: 'Credits',
    cells: ['50 / mo', '750 / seat / mo', '1,500 / seat / mo', 'Custom'],
  },
  { label: 'Searches', cells: ['5', '1,000 / mo', 'Unlimited', 'Unlimited'] },
  { label: 'Seats', cells: ['Up to 3', 'Any number', 'Any number', 'Custom'] },
  { label: 'Waterfall enrichment and Clu', cells: [true, true, true, true] },
  { label: 'Email sequences and call tasks', cells: [false, true, true, true] },
  { label: 'LinkedIn sequences', cells: [false, false, true, true] },
  { label: 'Active agents', cells: [false, false, '5', 'Unlimited'] },
  { label: 'HubSpot and Salesforce sync', cells: [false, false, true, true] },
  {
    label: 'CRM automation and in-CRM enrichment',
    cells: [false, false, true, true],
  },
  {
    label: 'Clu in Slack, knowledge and connectors',
    cells: [false, false, true, true],
  },
  {
    label: 'API, analytics and team reports',
    cells: [false, false, true, true],
  },
  { label: 'Dedicated support', cells: [false, false, false, true] },
];

function Value({ cell }: { cell: Cell }) {
  if (cell === true) {
    return (
      <span className='inline-flex h-5 w-5 items-center justify-center rounded-full bg-foreground/[0.07] text-foreground'>
        <AppIcon icon={iconCheck} className='h-2.5 w-2.5' />
        <span className='sr-only'>Included</span>
      </span>
    );
  }
  if (cell === false) {
    return (
      <span className='text-muted-foreground/40'>
        <span aria-hidden='true'>—</span>
        <span className='sr-only'>Not included</span>
      </span>
    );
  }
  return <span>{cell}</span>;
}

export function ComparePlansDialog({
  open,
  onOpenChange,
  plan = 'Pro',
  trial = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The column to highlight: the plan on offer. */
  plan?: string;
  /** The highlighted plan is the workspace's trial. */
  trial?: boolean;
}) {
  const highlighted = PLANS.findIndex(
    (name) => name.toLowerCase() === plan.trim().toLowerCase()
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl gap-0 p-0'>
        <DialogHeader className='px-6 pt-6 pb-4'>
          <DialogTitle className='text-xl font-semibold tracking-tight'>
            Compare plans
          </DialogTitle>
        </DialogHeader>
        <div className='px-6 pb-6'>
          <div className='overflow-hidden rounded-xl border border-border'>
            <table className='w-full text-[13px]'>
              <thead>
                <tr>
                  <th className='w-[34%] px-4 py-3' />
                  {PLANS.map((name, i) => (
                    <th
                      key={name}
                      className={cn(
                        'px-3 py-3 text-center text-sm font-semibold text-foreground',
                        i === highlighted && 'bg-primary/[0.06]'
                      )}
                    >
                      {name}
                      {trial && i === highlighted ? (
                        <span className='mt-0.5 block text-[11px] font-medium text-primary'>
                          Your trial
                        </span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <tr key={row.label} className='border-t border-border'>
                    <th
                      scope='row'
                      className='px-4 py-2.5 text-left font-normal text-foreground/80'
                    >
                      {row.label}
                    </th>
                    {row.cells.map((cell, i) => (
                      <td
                        key={PLANS[i]}
                        className={cn(
                          'px-3 py-2.5 text-center text-foreground',
                          i === highlighted && 'bg-primary/[0.06] font-medium'
                        )}
                      >
                        <Value cell={cell} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
